import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../../components/ui/dialog";
import { api } from "../../../lib/api";
import { toast } from "sonner";

/** Write-off — mandatory reason. Logs as a movement with kind=write_off. */
export default function WriteOffDialog({ open, onOpenChange, items, sites, onSuccess }) {
  const [itemId, setItemId] = useState("");
  const [site, setSite] = useState("");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setItemId(""); setSite(""); setQty(""); setReason(""); setBusy(false); };
  const pickedItem = items.find((i) => i.item_id === itemId);

  const submit = async () => {
    if (!itemId || !site || !qty) return toast.error("Item, site and quantity are required");
    if (!reason.trim()) return toast.error("Reason is mandatory for write-offs");
    const q = parseInt(qty, 10);
    if (!q || q <= 0) return toast.error("Quantity must be positive");

    setBusy(true);
    try {
      await api.post("/inventory/stock/write-off", {
        item_id: itemId, site, quantity: q, reason: reason.trim(),
      });
      reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Write-off failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="write-off-dialog">
        <DialogHeader>
          <DialogTitle>Write-off stock</DialogTitle>
          <DialogDescription>
            Permanently removes the specified quantity from a site. Reason is required and is recorded in the movement ledger.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Field label="Item">
            <select data-testid="wo-item" value={itemId} onChange={(e) => setItemId(e.target.value)} className={inputCls}>
              <option value="">Select…</option>
              {items.map((i) => <option key={i.item_id} value={i.item_id}>{i.name} ({i.unit})</option>)}
            </select>
            {pickedItem && (
              <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded-full px-2.5 py-1" data-testid="wo-item-label">
                <span>●</span>
                <span className="truncate">{pickedItem.name}</span>
                <span className="text-slate-400 font-normal">· {pickedItem.unit}</span>
              </div>
            )}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Site">
              <select data-testid="wo-site" value={site} onChange={(e) => setSite(e.target.value)} className={inputCls}>
                <option value="">Select…</option>
                {sites.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Quantity">
              <input data-testid="wo-qty" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className={inputCls}/>
            </Field>
          </div>
          <Field label="Reason (required)">
            <textarea data-testid="wo-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls + " resize-none"} placeholder="e.g. Broken in transit / spoilage / damaged"/>
          </Field>
        </div>
        <DialogFooter className="mt-4">
          <button data-testid="wo-cancel" onClick={() => onOpenChange(false)} className="rounded-full px-4 py-2 text-xs font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">Cancel</button>
          <button data-testid="wo-submit" onClick={submit} disabled={busy} className="rounded-full px-4 py-2 text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">{busy ? "Saving…" : "Write off"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30";
const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    {children}
  </label>
);
